import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Table, TableModule, TableRowReorderEvent } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import {
  Route,
  RouteSection,
  RouteSectionMetadata,
  StoreRouteSectionSchema,
  UpdateRouteSectionSchema,
} from '../../../../shared/models/route.model';
import { RouteService } from '../../../../shared/services/route.service';

@Component({
  selector: 'app-route-section-table',
  imports: [
    CommonModule,
    DatePipe,
    TableModule,
    TranslateModule,
    ButtonModule,
    FormsModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
  ],
  templateUrl: './route-section-table.component.html',
  styleUrl: './route-section-table.component.scss',
})
export class RouteSectionTableComponent {
  @ViewChild('sectionsTable') sectionsTable!: Table;
  #router = inject(Router);
  #routeService = inject(RouteService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);

  route = input.required<Route>();
  sectionChanged = output<void>();

  public routeSectionColumns = RouteSectionMetadata.routeSectionColumns;

  editingSections = signal<Map<string, RouteSection>>(new Map());
  newRowTrackingKeys = signal<Set<string>>(new Set());
  invalidFieldsMap = signal<Map<string, Set<string>>>(new Map());

  typeOptions = [
    {
      label: this.#translateService.instant('routeSection.typeElement'),
      value: 'element',
    },
    {
      label: this.#translateService.instant('routeSection.typePlatform'),
      value: 'platform',
    },
  ];

  displaySections = computed(() => {
    const original = this.route()?.sections ?? [];
    const editing = this.editingSections();
    const trackingKeys = this.newRowTrackingKeys();

    const newRows = Array.from(trackingKeys)
      .filter((key) => editing.has(key))
      .map((key) => editing.get(key)!);

    const existingRows = original.map(
      (section) => editing.get(section.id) || section,
    );

    return [...newRows, ...existingRows].sort((a, b) => a.order - b.order);
  });

  onRowCreateInit() {
    const trackingKey = `new_${Date.now()}`;
    const sections = this.route()?.sections ?? [];
    const maxOrder = sections.reduce(
      (max, s) => Math.max(max, s.order),
      0,
    );
    const newRow: RouteSection = {
      id: trackingKey,
      route_id: this.route().id,
      name: '',
      type: 'element',
      order: maxOrder + 1,
      last_checked_at: null,
      has_defects: false,
    };

    this.newRowTrackingKeys.update((set) => new Set(set).add(trackingKey));
    this.editingSections.update((map) => {
      const newMap = new Map(map);
      newMap.set(trackingKey, newRow);
      return newMap;
    });

    if (this.sectionsTable) {
      this.sectionsTable.initRowEdit(newRow);
    }
  }

  onRowEditInit(section: RouteSection) {
    this.invalidFieldsMap.update((map) => {
      const newMap = new Map(map);
      newMap.delete(section.id);
      return newMap;
    });
    this.editingSections.update((map) => {
      const newMap = new Map(map);
      newMap.set(section.id, { ...section });
      return newMap;
    });
  }

  onRowEditSave(section: RouteSection) {
    const isNew = this.newRowTrackingKeys().has(section.id);
    const routeId = this.route().id;
    const trackingKey = section.id;

    const data = {
      name: section.name,
      type: section.type,
      order: section.order,
    };
    const schema = isNew ? StoreRouteSectionSchema : UpdateRouteSectionSchema;
    const result = schema.safeParse(data);

    if (!result.success) {
      const fields = new Set(
        result.error.issues.map((i) => i.path[0]?.toString()).filter(Boolean),
      );
      this.invalidFieldsMap.update((map) => {
        const newMap = new Map(map);
        newMap.set(trackingKey, fields as Set<string>);
        return newMap;
      });
      this.#messageService.add({
        severity: 'warn',
        summary: this.#translateService.instant('label.errorHeader'),
        detail: this.#translateService.instant(
          result.error.issues[0]?.code === 'too_small'
            ? 'validation.required'
            : 'validation.invalid',
        ),
      });
      return;
    }

    this.invalidFieldsMap.update((map) => {
      const newMap = new Map(map);
      newMap.delete(trackingKey);
      return newMap;
    });

    if (isNew) {
      this.#routeService.createRouteSection(routeId, data).subscribe({
        next: () => {
          delete this.sectionsTable.editingRowKeys[trackingKey];
          this.newRowTrackingKeys.update((set) => {
            const newSet = new Set(set);
            newSet.delete(trackingKey);
            return newSet;
          });
          this.editingSections.update((map) => {
            const newMap = new Map(map);
            newMap.delete(trackingKey);
            return newMap;
          });
          this.sectionChanged.emit();
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('routeSection.created'),
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('routeSection.createError'),
          });
        },
      });
    } else {
      this.#routeService
        .updateRouteSection(routeId, section.id, data)
        .subscribe({
          next: () => {
            delete this.sectionsTable.editingRowKeys[section.id];
            this.editingSections.update((map) => {
              const newMap = new Map(map);
              newMap.delete(section.id);
              return newMap;
            });
            this.sectionChanged.emit();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant('label.successHeader'),
              detail: this.#translateService.instant('routeSection.updated'),
            });
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant(
                'routeSection.updateError',
              ),
            });
          },
        });
    }
  }

  onRowEditCancel(section: RouteSection) {
    const trackingKey = section.id;
    this.invalidFieldsMap.update((map) => {
      const newMap = new Map(map);
      newMap.delete(trackingKey);
      return newMap;
    });
    this.newRowTrackingKeys.update((set) => {
      const newSet = new Set(set);
      newSet.delete(trackingKey);
      return newSet;
    });
    this.editingSections.update((map) => {
      const newMap = new Map(map);
      newMap.delete(trackingKey);
      return newMap;
    });
  }

  onRowReorder(event: TableRowReorderEvent) {
    const reordered = this.sectionsTable.value as RouteSection[];
    const routeId = this.route().id;
    const hasNewRows = this.newRowTrackingKeys().size > 0;

    // Recalculate local order starting from 1
    reordered.forEach((section, index) => {
      section.order = index + 1;
    });

    // When new rows exist, only update locally — order is sent on save
    if (hasNewRows) {
      this.editingSections.update((map) => {
        const newMap = new Map(map);
        for (const key of this.newRowTrackingKeys()) {
          const editingRow = newMap.get(key);
          if (editingRow) {
            const reorderedRow = reordered.find((s) => s.id === key);
            if (reorderedRow) {
              newMap.set(key, { ...editingRow, order: reorderedRow.order });
            }
          }
        }
        return newMap;
      });
      return;
    }

    // Send a single update for the dragged section — API reorders the rest
    const dragIndex = event.dragIndex!;
    const dropIndex = event.dropIndex!;
    if (dragIndex === dropIndex) return;

    const movedSection = reordered[dropIndex];
    this.#routeService
      .updateRouteSection(routeId, movedSection.id, {
        order: movedSection.order,
      })
      .subscribe({
        next: () => {
          this.sectionChanged.emit();
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('routeSection.updated'),
          });
        },
        error: () => {
          this.sectionChanged.emit();
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant(
              'routeSection.updateError',
            ),
          });
        },
      });
  }

  navigateToCreateDefect(section: RouteSection) {
    this.#router.navigate(['/defects/new'], {
      queryParams: {
        modelType: 'route_section',
        modelId: section.id,
        returnUrl: '/routes',
      },
    });
  }

  navigateToDefects(section: RouteSection) {
    this.#router.navigate(['/defects'], {
      queryParams: { modelName: `${this.route().name} - ${section.name}` },
    });
  }

  navigateToInspections(section: RouteSection) {
    this.#router.navigate(['/inspections'], {
      queryParams: {
        modelType: 'route_section',
        modelId: section.id,
        modelName: `${this.route().name} - ${section.name}`,
      },
    });
  }

  onRowDeleteInit(event: Event, section: RouteSection) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.#translateService.instant(
        'routeSection.deleteConfirmMessage',
      ),
      header: this.#translateService.instant('routeSection.deleteHeader'),
      rejectLabel: this.#translateService.instant('label.cancel'),
      rejectButtonProps: {
        label: this.#translateService.instant('label.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.#translateService.instant('label.delete'),
        severity: 'danger',
      },
      accept: () => {
        this.#routeService
          .deleteRouteSection(this.route().id, section.id)
          .subscribe({
            next: () => {
              this.sectionChanged.emit();
              this.#messageService.add({
                severity: 'success',
                summary: this.#translateService.instant(
                  'label.successfullyDeletedHeader',
                ),
                detail: this.#translateService.instant(
                  'label.successfullyDeleted',
                ),
              });
            },
            error: () => {
              this.#messageService.add({
                severity: 'error',
                summary: this.#translateService.instant('label.errorHeader'),
                detail: this.#translateService.instant('label.errorDeleting'),
              });
            },
          });
      },
      reject: () => {
        //nothing
      },
    });
  }
}
